/**
 * useThemeCatalog — real-time subscription to
 * projects/{pid}/catalogs/theme_catalog.{themes[]}.
 *
 * Returns the per-project ContentTheme[] + helpers to mutate it:
 *   saveThemes(themes)  — overwrite the whole catalog
 *   appendThemes(themes) — merge (dedup by id) — used after AI generator
 *
 * Used by ThemeCatalogDialog + GenerateTopicsDialog live dropdown.
 */

import { useCallback, useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { ContentTheme } from '../types';

export interface UseThemeCatalogReturn {
  themes: ContentTheme[];
  loading: boolean;
  error: Error | null;
  saveThemes: (next: ContentTheme[]) => Promise<void>;
  appendThemes: (fresh: ContentTheme[]) => Promise<void>;
}

function catalogRef(projectId: string) {
  const db = CoreFirebaseService.getInstance().firestore;
  return doc(db, 'projects', projectId, 'catalogs', 'theme_catalog');
}

export function useThemeCatalog(projectId: string | null): UseThemeCatalogReturn {
  const [themes, setThemes] = useState<ContentTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId) {
      setThemes([]); setLoading(false); return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      catalogRef(projectId),
      (snap) => {
        const data = snap.data() as { themes?: ContentTheme[] } | undefined;
        setThemes(data?.themes ?? []);
        setLoading(false);
      },
      (err) => { setError(err as Error); setLoading(false); },
    );
    return () => unsub();
  }, [projectId]);

  const saveThemes = useCallback(async (next: ContentTheme[]) => {
    if (!projectId) return;
    await setDoc(catalogRef(projectId), { themes: next, updatedAt: serverTimestamp() }, { merge: true });
  }, [projectId]);

  const appendThemes = useCallback(async (fresh: ContentTheme[]) => {
    if (!projectId) return;
    const existingIds = new Set(themes.map((t) => t.id));
    const merged = [...themes, ...fresh.filter((t) => !existingIds.has(t.id))];
    await saveThemes(merged);
  }, [projectId, themes, saveThemes]);

  return { themes, loading, error, saveThemes, appendThemes };
}
